<template>
  <div class="ngo-upload-page">
    <section class="upload-hero">
      <div class="container upload-hero__content">
        <div class="upload-hero__copy">
          <p class="upload-kicker">Admin Upload</p>
          <h1>Upload organizations with Excel</h1>
          <p class="upload-description">
            Import organization records in bulk using a spreadsheet file. This workspace is meant
            for admin-led uploads of NGO data in `.xlsx`, `.xls`, or `.csv` format.
          </p>
        </div>

        <div class="upload-hero__summary">
          <div class="summary-card">
            <span class="summary-card__label">Supported files</span>
            <strong class="summary-card__value">Excel and CSV</strong>
          </div>
          <div class="summary-card">
            <span class="summary-card__label">Audience</span>
            <strong class="summary-card__value">Admin users only</strong>
          </div>
        </div>
      </div>
    </section>

    <section class="container upload-workspace">
      <div class="upload-panel">
        <div class="upload-panel__header">
          <div class="upload-panel__intro">
            <p class="section-kicker">Spreadsheet Import</p>
            <h2>Upload organization workbook</h2>
            <p>
              Choose the spreadsheet that contains the organizations you want to import into the
              platform.
            </p>
          </div>
          <div class="upload-panel__tools">
            <div class="template-card">
              <span class="template-card__label">Quick start</span>
              <strong>Use the template sheet</strong>
              <p>Download the sample workbook, update the rows, then upload it here.</p>
              <button
                type="button"
                class="upload-button upload-button--ghost upload-button--template"
                @click="downloadTemplate"
              >
                Download template
              </button>
            </div>
          </div>
        </div>

        <label class="file-dropzone" :class="{ 'file-dropzone--selected': !!selectedFile }">
          <input
            ref="fileInput"
            class="file-dropzone__input"
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            @change="handleFileSelection"
          />

          <div class="file-dropzone__icon">
            <i class="fas fa-file-excel" aria-hidden="true"></i>
          </div>

          <div class="file-dropzone__copy">
            <h3>{{ selectedFile ? 'Spreadsheet selected' : 'Choose an Excel file to upload' }}</h3>
            <p v-if="selectedFile">{{ selectedFile.name }} · {{ formattedFileSize }}</p>
            <p v-else>
              Drag and drop is optional. You can also click here to browse for an Excel or CSV file.
            </p>
          </div>
        </label>

        <div class="upload-meta-grid">
          <article class="upload-meta-card">
            <span class="upload-meta-card__label">Accepted formats</span>
            <strong>.xlsx, .xls, .csv</strong>
          </article>
          <article class="upload-meta-card">
            <span class="upload-meta-card__label">Selected file</span>
            <strong>{{ selectedFile ? selectedFile.name : 'No file selected' }}</strong>
          </article>
          <article class="upload-meta-card">
            <span class="upload-meta-card__label">Upload status</span>
            <strong>{{ selectedFile ? 'Ready for upload' : 'Waiting for file' }}</strong>
          </article>
        </div>

        <div class="upload-guidance">
          <p class="upload-guidance__title">Before you upload</p>
          <ul>
            <li>Use one row per organization record.</li>
            <li>Keep column headings consistent with the expected import template.</li>
            <li>The template includes 13 columns; Year of Registration may be omitted.</li>
            <li>Keep tracking IDs, file numbers, and phone numbers as text.</li>
          </ul>
        </div>

        <div v-if="selectedFile" class="validation-panel">
          <div class="validation-panel__header">
            <div>
              <p class="section-kicker">Sheet Validation</p>
              <h3>Required columns check</h3>
            </div>
            <span
              class="validation-pill"
              :class="
                hasSpreadsheetValidationErrors
                  ? 'validation-pill--danger'
                  : 'validation-pill--success'
              "
            >
              {{ hasSpreadsheetValidationErrors ? 'Check spreadsheet' : 'Sheet verified' }}
            </span>
          </div>

          <div class="validation-grid">
            <article class="validation-card">
              <span class="validation-card__label">Expected columns</span>
              <strong>{{ requiredColumns.length }}</strong>
              <p>{{ requiredColumns.join(', ') }}</p>
            </article>

            <article class="validation-card">
              <span class="validation-card__label">Detected columns</span>
              <strong>{{ uploadedHeaders.length }}</strong>
              <p>
                {{
                  uploadedHeaders.length ? uploadedHeaders.join(', ') : 'No columns detected yet'
                }}
              </p>
            </article>
          </div>

          <div
            v-if="missingRequiredColumns.length"
            class="validation-alert validation-alert--danger"
          >
            Missing required columns: {{ missingRequiredColumns.join(', ') }}
          </div>
          <div
            v-else-if="uploadedHeaders.length && !duplicateHeaders.length"
            class="validation-alert validation-alert--success"
          >
            All required columns are present in the selected sheet.
          </div>
          <div v-if="duplicateHeaders.length" class="validation-alert validation-alert--danger">
            Duplicate columns: {{ duplicateHeaders.join(', ') }}. Keep each column only once.
          </div>
        </div>

        <div v-if="previewRows.length" class="preview-panel">
          <div class="preview-panel__header">
            <div>
              <p class="section-kicker">Spreadsheet Preview</p>
              <h3>Review extracted rows before upload</h3>
            </div>
            <span class="preview-pill"
              >{{ previewRows.length }} row{{ previewRows.length === 1 ? '' : 's' }}</span
            >
          </div>

          <div class="preview-table-shell">
            <b-table
              id="upload-preview-table"
              small
              responsive
              :items="paginatedPreviewRows"
              :fields="previewFields"
              class="preview-table"
              show-empty
            >
              <template #empty>
                <div class="preview-empty">No preview rows available.</div>
              </template>
            </b-table>
          </div>

          <div v-if="previewRows.length" class="preview-pagination-shell">
            <div class="preview-pagination-copy">
              <label class="preview-page-size-shell" for="preview-page-size">
                <span>Rows</span>
                <select id="preview-page-size" v-model.number="previewPerPage">
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </label>
              <p>Page {{ previewCurrentPage }} of {{ previewTotalPages }}</p>
              <span>{{ previewStartRow }} - {{ previewEndRow }} of {{ previewRows.length }}</span>
            </div>

            <b-pagination
              v-model="previewCurrentPage"
              :total-rows="previewRows.length"
              :per-page="previewPerPage"
              pills
              align="right"
              aria-controls="upload-preview-table"
            />
          </div>
        </div>

        <label v-if="previewRows.length" class="review-confirmation">
          <input v-model="reviewConfirmed" type="checkbox" />
          <span>I have reviewed the spreadsheet preview and I’m ready to upload it.</span>
        </label>

        <div class="upload-actions">
          <button type="button" class="upload-button upload-button--ghost" @click="resetSelection">
            Clear selection
          </button>
          <button
            type="button"
            class="upload-button upload-button--primary"
            :disabled="!canUploadSpreadsheet"
            @click="submitUpload"
          >
            <b-spinner v-if="uploading" small />
            <span v-else>Upload spreadsheet</span>
          </button>
        </div>

        <div
          v-if="loadingBatchList || batchList.length || importBatch.id"
          class="batch-progress-panel"
        >
          <div class="batch-progress-panel__header">
            <div>
              <p class="section-kicker">Import Batch</p>
              <h3>Batch processing metrics</h3>
              <p v-if="importBatch.id" class="batch-progress-panel__description">
                Batch ID: <strong>{{ importBatch.id }}</strong>
              </p>
              <p v-else class="batch-progress-panel__description">
                Select an import batch to review its current status and totals.
              </p>
            </div>
            <span v-if="importBatch.id" class="batch-status-pill" :class="batchStatusClass">
              {{ importBatch.status || 'queued' }}
            </span>
          </div>

          <div class="batch-selector-shell">
            <label class="batch-selector-field" for="import-batch-select">
              <span class="batch-selector-field__label">Batch ID</span>
              <select
                id="import-batch-select"
                v-model="selectedBatchId"
                :disabled="loadingBatchList || !batchList.length"
              >
                <option value="" disabled>
                  {{ loadingBatchList ? 'Loading batches...' : 'Select a batch' }}
                </option>
                <option
                  v-for="batchOption in normalizedBatchOptions"
                  :key="batchOption.id"
                  :value="batchOption.id"
                >
                  {{ batchOption.label }}
                </option>
              </select>
            </label>

            <button
              type="button"
              class="upload-button upload-button--ghost batch-selector-button"
              :disabled="loadingBatchList"
              @click="fetchImportBatches({ preserveSelection: true })"
            >
              {{ loadingBatchList ? 'Refreshing...' : 'Refresh batches' }}
            </button>

            <button
              v-if="importBatch.id && canPollSelectedBatch"
              type="button"
              class="upload-button batch-selector-button"
              :class="isBatchPollingActive ? 'upload-button--danger' : 'upload-button--primary'"
              @click="toggleBatchPolling"
            >
              {{ isBatchPollingActive ? 'Stop polling' : 'Resume polling' }}
            </button>
          </div>

          <div v-if="!loadingBatchList && !importBatch.id" class="batch-empty-state">
            No import batch has been selected yet.
          </div>

          <template v-else-if="importBatch.id">
            <div class="batch-progress-bar">
              <div class="batch-progress-bar__track">
                <div
                  class="batch-progress-bar__fill"
                  :style="{ width: `${safeProgressPercentage}%` }"
                ></div>
              </div>
              <strong>{{ safeProgressPercentage }}%</strong>
            </div>

            <div class="batch-metrics-grid">
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Total rows</span>
                <strong>{{ importBatch.total_rows }}</strong>
              </article>
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Processed rows</span>
                <strong>{{ importBatch.processed_rows }}</strong>
              </article>
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Created</span>
                <strong>{{ importBatch.created_count }}</strong>
              </article>
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Updated</span>
                <strong>{{ importBatch.updated_count }}</strong>
              </article>
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Skipped</span>
                <strong>{{ importBatch.skipped_count }}</strong>
              </article>
              <article class="batch-metric-card">
                <span class="batch-metric-card__label">Errors</span>
                <strong>{{ importBatch.error_count }}</strong>
              </article>
            </div>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as XLSX from 'xlsx';
import {
  getOrganizationImportBatch,
  getOrganizationImportBatches,
  uploadOrganizationsSpreadsheet,
} from '../../config/api-modules/api.organization';

export default {
  name: 'NgoUploadPage',
  data() {
    return {
      selectedFile: null,
      uploading: false,
      previewRows: [],
      previewFields: [],
      previewCurrentPage: 1,
      previewPerPage: 20,
      uploadedHeaders: [],
      reviewConfirmed: false,
      loadingBatchList: false,
      batchList: [],
      selectedBatchId: '',
      importBatch: {
        id: '',
        status: '',
        total_rows: 0,
        processed_rows: 0,
        created_count: 0,
        updated_count: 0,
        skipped_count: 0,
        error_count: 0,
        progress_percentage: 0,
      },
      batchPollingTimeoutId: null,
      batchPollingStoppedManually: false,
      templateColumns: [
        'S/N',
        'TRACKING ID',
        'FILE. NO',
        'NAME OF NGO',
        'AREA OF FOCUS',
        'NAME OF CONTACT PERSON',
        'YEAR OF REGISTRATION',
        'YEAR RENEWED',
        'LOCATION AND ADDRESS',
        'PHONE NO. OF CONTACT PERSON',
        'EMAIL ADDRESS',
        'WEBSITE',
        'STATE',
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),

    requiredColumns() {
      return this.templateColumns.filter((column) => column !== 'YEAR OF REGISTRATION');
    },

    canUploadNgo() {
      return [1, 2, 5, 6].includes(Number(this.user && this.user.type));
    },

    formattedFileSize() {
      if (!this.selectedFile || !this.selectedFile.size) {
        return '0 KB';
      }

      const sizeInKb = this.selectedFile.size / 1024;
      if (sizeInKb < 1024) {
        return `${sizeInKb.toFixed(1)} KB`;
      }

      return `${(sizeInKb / 1024).toFixed(2)} MB`;
    },
    missingRequiredColumns() {
      const normalizedHeaders = this.uploadedHeaders.map((header) => this.normalizeHeader(header));
      return this.requiredColumns.filter(
        (column) => !normalizedHeaders.includes(this.normalizeHeader(column))
      );
    },
    duplicateHeaders() {
      const seen = new Set();
      return this.uploadedHeaders.filter((header) => {
        const normalized = this.normalizeHeader(header);
        if (seen.has(normalized)) return true;
        seen.add(normalized);
        return false;
      });
    },
    hasSpreadsheetValidationErrors() {
      return this.missingRequiredColumns.length > 0 || this.duplicateHeaders.length > 0;
    },
    canUploadSpreadsheet() {
      return (
        !!this.selectedFile &&
        !!this.previewRows.length &&
        !this.hasSpreadsheetValidationErrors &&
        this.reviewConfirmed &&
        !this.uploading
      );
    },
    safeProgressPercentage() {
      const percentage = Number(this.importBatch.progress_percentage || 0);
      return Number.isFinite(percentage) ? Math.max(0, Math.min(100, percentage)) : 0;
    },
    paginatedPreviewRows() {
      const start = (this.previewCurrentPage - 1) * this.previewPerPage;
      return this.previewRows.slice(start, start + this.previewPerPage);
    },
    previewTotalPages() {
      return Math.max(1, Math.ceil(this.previewRows.length / this.previewPerPage));
    },
    previewStartRow() {
      if (!this.previewRows.length) {
        return 0;
      }

      return (this.previewCurrentPage - 1) * this.previewPerPage + 1;
    },
    previewEndRow() {
      if (!this.previewRows.length) {
        return 0;
      }

      return Math.min(this.previewCurrentPage * this.previewPerPage, this.previewRows.length);
    },
    batchStatusClass() {
      const status = String(this.importBatch.status || '').toLowerCase();

      if (status === 'completed') {
        return 'batch-status-pill--success';
      }

      if (['failed', 'error'].includes(status)) {
        return 'batch-status-pill--danger';
      }

      return 'batch-status-pill--processing';
    },
    normalizedBatchOptions() {
      return this.batchList.map((batch) => ({
        id: String(batch.id || ''),
        label: this.formatBatchOptionLabel(batch),
      }));
    },
    canPollSelectedBatch() {
      const status = String(this.importBatch.status || '').toLowerCase();
      return !!this.importBatch.id && !['completed', 'failed', 'error'].includes(status);
    },
    isBatchPollingActive() {
      return !!this.batchPollingTimeoutId && !this.batchPollingStoppedManually;
    },
  },
  async beforeMount() {
    if (!this.canUploadNgo) {
      this.$router.push('/ngo/landing');
      return;
    }

    await this.fetchImportBatches();
  },
  beforeDestroy() {
    this.stopBatchPolling();
  },
  watch: {
    previewPerPage() {
      this.previewCurrentPage = 1;
    },
    selectedBatchId(batchId) {
      this.batchPollingStoppedManually = false;
      if (!batchId) {
        this.resetImportBatch();
        return;
      }

      this.loadImportBatch(batchId, {
        showErrorToast: true,
        schedulePolling: true,
      });
    },
  },
  methods: {
    getBatchListPayload(response) {
      const payload = response?.data;

      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.results)) {
        return payload.results;
      }

      return [];
    },
    formatBatchOptionLabel(batch = {}) {
      const batchId = batch.id || 'Unknown';
      const status = String(batch.status || 'pending').toLowerCase();
      const totalRows = Number(batch.total_rows || 0);
      const createdAt =
        batch.created_at || batch.created || batch.created_on || batch.date_created || '';

      let dateLabel = '';
      if (createdAt) {
        const parsedDate = new Date(createdAt);
        if (!Number.isNaN(parsedDate.getTime())) {
          dateLabel = parsedDate.toLocaleString();
        }
      }

      const parts = [`Batch ${batchId}`, status, `${totalRows} rows`];
      if (dateLabel) {
        parts.push(dateLabel);
      }

      return parts.join(' · ');
    },
    async fetchImportBatches({ preserveSelection = true } = {}) {
      this.loadingBatchList = true;

      try {
        const response = await getOrganizationImportBatches();
        const batches = this.getBatchListPayload(response);
        this.batchList = batches;

        const hasCurrentSelection = preserveSelection
          ? batches.some((batch) => String(batch.id) === String(this.selectedBatchId))
          : false;

        if (hasCurrentSelection) {
          return;
        }

        if (!preserveSelection) {
          this.selectedBatchId = '';
          this.resetImportBatch();
        }
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to fetch import batch list',
        });
      } finally {
        this.loadingBatchList = false;
      }
    },
    isValidSpreadsheet(file) {
      if (!file || !file.name) {
        return false;
      }

      const extension = file.name.split('.').pop().toLowerCase();
      return ['xlsx', 'xls', 'csv'].includes(extension);
    },
    handleFileSelection(event) {
      const [file] = event.target.files || [];

      if (!file) {
        this.selectedFile = null;
        this.previewRows = [];
        this.previewFields = [];
        this.previewCurrentPage = 1;
        this.uploadedHeaders = [];
        this.reviewConfirmed = false;
        return;
      }

      if (!this.isValidSpreadsheet(file)) {
        const input = this.$refs.fileInput || event.target;
        if (input) {
          input.value = '';
        }
        this.selectedFile = null;
        this.previewRows = [];
        this.previewFields = [];
        this.previewCurrentPage = 1;
        this.uploadedHeaders = [];
        this.reviewConfirmed = false;
        this.$toast.show({
          type: 'ERROR',
          message: 'Please select a valid Excel or CSV file',
        });
        return;
      }

      this.selectedFile = file;
      this.reviewConfirmed = false;
      this.readSpreadsheet(file);
    },
    resetSelection() {
      this.selectedFile = null;
      this.previewRows = [];
      this.previewFields = [];
      this.previewCurrentPage = 1;
      this.uploadedHeaders = [];
      this.reviewConfirmed = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    resetImportBatch() {
      this.stopBatchPolling();
      this.batchPollingStoppedManually = false;
      this.importBatch = {
        id: '',
        status: '',
        total_rows: 0,
        processed_rows: 0,
        created_count: 0,
        updated_count: 0,
        skipped_count: 0,
        error_count: 0,
        progress_percentage: 0,
      };
    },
    stopBatchPolling({ manual = false } = {}) {
      if (this.batchPollingTimeoutId) {
        clearTimeout(this.batchPollingTimeoutId);
        this.batchPollingTimeoutId = null;
      }

      if (manual) {
        this.batchPollingStoppedManually = true;
      }
    },
    scheduleBatchPolling(batchId) {
      this.stopBatchPolling();
      this.batchPollingStoppedManually = false;
      this.batchPollingTimeoutId = setTimeout(() => {
        this.loadImportBatch(batchId, {
          showErrorToast: false,
          schedulePolling: true,
        });
      }, 30000);
    },
    updateImportBatch(batch = {}) {
      this.importBatch = {
        id: batch.id || this.importBatch.id || '',
        status: batch.status || this.importBatch.status || '',
        total_rows: Number(batch.total_rows || 0),
        processed_rows: Number(batch.processed_rows || 0),
        created_count: Number(batch.created_count || 0),
        updated_count: Number(batch.updated_count || 0),
        skipped_count: Number(batch.skipped_count || 0),
        error_count: Number(batch.error_count || 0),
        progress_percentage: Number(batch.progress_percentage || 0),
      };
    },
    async loadImportBatch(batchId, options = {}) {
      const { showErrorToast = true, schedulePolling = true } = options;

      try {
        const response = await getOrganizationImportBatch(batchId);
        const batch = response?.data || {};
        this.updateImportBatch(batch);
        this.syncBatchListEntry(batch);

        const normalizedStatus = String(batch.status || '').toLowerCase();
        if (['completed', 'failed', 'error'].includes(normalizedStatus)) {
          this.stopBatchPolling();
          return;
        }

        if (
          schedulePolling &&
          !this.batchPollingStoppedManually &&
          String(this.selectedBatchId) === String(batchId)
        ) {
          this.scheduleBatchPolling(batchId);
        }
      } catch (error) {
        this.stopBatchPolling();
        if (showErrorToast) {
          this.$toast.show({
            type: 'ERROR',
            message:
              error?.response?.data?.message ||
              error?.response?.data?.detail ||
              'Unable to fetch import batch status',
          });
        }
      }
    },
    syncBatchListEntry(batch = {}) {
      const batchId = String(batch.id || '');
      if (!batchId) {
        return;
      }

      const existingIndex = this.batchList.findIndex(
        (existingBatch) => String(existingBatch.id) === batchId
      );

      if (existingIndex >= 0) {
        this.batchList.splice(existingIndex, 1, {
          ...this.batchList[existingIndex],
          ...batch,
        });
        return;
      }

      this.batchList.unshift(batch);
    },
    toggleBatchPolling() {
      if (!this.importBatch.id || !this.canPollSelectedBatch) {
        return;
      }

      if (this.isBatchPollingActive) {
        this.stopBatchPolling({ manual: true });
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Batch metrics polling stopped',
        });
        return;
      }

      this.batchPollingStoppedManually = false;
      this.scheduleBatchPolling(this.importBatch.id);
      this.$toast.show({
        type: 'SUCCESS',
        message: 'Batch metrics polling resumed. Refresh interval is 30 seconds',
      });
    },
    normalizeHeader(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
    },
    async readSpreadsheet(file) {
      this.previewRows = [];
      this.previewFields = [];
      this.uploadedHeaders = [];
      this.previewCurrentPage = 1;
      this.reviewConfirmed = false;
      try {
        const buffer = await file.arrayBuffer();
        if (this.selectedFile !== file) return;
        const workbook = XLSX.read(buffer, { type: 'array' });
        const [firstSheetName] = workbook.SheetNames;

        if (!firstSheetName) {
          this.previewRows = [];
          this.previewFields = [];
          this.previewCurrentPage = 1;
          this.$toast.show({
            type: 'ERROR',
            message: 'The selected spreadsheet does not contain any sheets',
          });
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          raw: false,
          blankrows: false,
        });

        const [headerRow = [], ...dataRows] = rows;
        const headers = headerRow.map((header) => String(header).trim());
        this.uploadedHeaders = headers.filter(Boolean);
        if (!dataRows.length) {
          this.previewRows = [];
          this.previewFields = [];
          this.previewCurrentPage = 1;
          this.reviewConfirmed = false;
          this.$toast.show({
            type: 'ERROR',
            message: 'The selected spreadsheet does not contain any data rows',
          });
          return;
        }

        this.previewFields = headers.map((header, index) => ({
          key: `column_${index}`,
          label: header,
        }));
        this.previewRows = dataRows.map((row, index) => ({
          row_number: index + 1,
          ...Object.fromEntries(headers.map((header, column) => [`column_${column}`, row[column]])),
        }));
        this.previewFields = [
          {
            key: 'row_number',
            label: '#',
          },
          ...this.previewFields,
        ];
        this.previewCurrentPage = 1;
        this.reviewConfirmed = false;
      } catch (error) {
        if (this.selectedFile !== file) return;
        this.previewRows = [];
        this.previewFields = [];
        this.previewCurrentPage = 1;
        this.uploadedHeaders = [];
        this.reviewConfirmed = false;
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to read the selected spreadsheet',
        });
      }
    },
    downloadTemplate() {
      const templateRow = {
        'S/N': 1,
        'TRACKING ID': 'FMOH/PSO/00001',
        'FILE. NO': 'MH.7305/S.001/1',
        'NAME OF NGO': 'Sample NGO',
        'AREA OF FOCUS': 'Primary healthcare; Nutrition',
        'NAME OF CONTACT PERSON': 'Jane Doe',
        'YEAR OF REGISTRATION': new Date().getFullYear(),
        'YEAR RENEWED': '',
        'LOCATION AND ADDRESS': '1 Sample Street, Abuja',
        'PHONE NO. OF CONTACT PERSON': '08000000000; +2348000000001',
        'EMAIL ADDRESS': 'sample@example.com',
        WEBSITE: '',
        STATE: 'ABUJA',
      };

      const worksheet = XLSX.utils.json_to_sheet([templateRow], {
        header: this.templateColumns,
      });
      const workbook = XLSX.utils.book_new();
      worksheet['!cols'] = [6, 24, 24, 34, 48, 28, 24, 18, 42, 34, 34, 30, 18].map((wch) => ({
        wch,
      }));
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Organizations');
      XLSX.writeFile(workbook, 'organization-upload-template.xlsx');
    },
    async submitUpload() {
      if (!this.selectedFile) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please select an Excel file before uploading',
        });
        return;
      }

      if (this.missingRequiredColumns.length) {
        this.$toast.show({
          type: 'ERROR',
          message: `Missing required columns: ${this.missingRequiredColumns.join(', ')}`,
        });
        return;
      }

      if (this.duplicateHeaders.length || !this.previewRows.length || this.uploading) {
        this.$toast.show({
          type: 'ERROR',
          message: this.duplicateHeaders.length
            ? `Duplicate columns: ${this.duplicateHeaders.join(', ')}`
            : 'Please wait for a valid spreadsheet preview before uploading',
        });
        return;
      }

      if (!this.reviewConfirmed) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please confirm that you have reviewed the spreadsheet before uploading',
        });
        return;
      }

      this.uploading = true;
      this.resetImportBatch();

      try {
        const response = await uploadOrganizationsSpreadsheet(this.selectedFile);
        const batch = response?.data?.batch;
        const batchId = batch?.id;

        if (!batchId) {
          throw new Error('Batch ID missing from upload response');
        }

        this.updateImportBatch({
          id: batchId,
          status: batch?.status || 'validated',
        });
        this.syncBatchListEntry({
          ...batch,
          id: batchId,
          status: batch?.status || 'validated',
        });
        this.selectedBatchId = String(batchId);

        this.$toast.show({
          type: 'SUCCESS',
          message: response?.data?.message || 'Spreadsheet uploaded successfully',
        });
        this.resetSelection();
        await this.fetchImportBatches({ preserveSelection: true });
        this.scheduleBatchPolling(batchId);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.message === 'Batch ID missing from upload response'
              ? 'Upload succeeded but no batch ID was returned'
              : error?.response?.data?.message ||
                error?.response?.data?.detail ||
                'Unable to upload spreadsheet',
        });
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.ngo-upload-page {
  min-height: 100vh;
  padding-bottom: 2rem;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.12), transparent 24%),
    linear-gradient(180deg, #f4faf5 0%, #eef5ef 100%);
}

.upload-hero {
  margin-bottom: 1.75rem;
  padding: 4.5rem 0 2.75rem;
  background: linear-gradient(135deg, rgba(9, 32, 17, 0.94), rgba(24, 78, 38, 0.84)),
    linear-gradient(180deg, #16391f 0%, #30ab4c 100%);
  border-radius: 0 0 30px 30px;
  box-shadow: 0 22px 60px rgba(17, 41, 24, 0.16);
}

.upload-hero__content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  color: #ffffff;
}

.upload-hero__copy {
  max-width: 44rem;
}

.upload-kicker,
.section-kicker,
.summary-card__label,
.upload-meta-card__label {
  margin-bottom: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.upload-kicker {
  color: rgba(255, 255, 255, 0.74);
}

.upload-hero h1 {
  margin: 0 0 0.9rem;
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  line-height: 1.05;
}

.upload-description {
  max-width: 42rem;
  margin: 0;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.84);
}

.upload-hero__summary {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-width: 17rem;
}

.summary-card {
  padding: 1rem 1.1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
}

.summary-card__label {
  display: block;
  color: rgba(255, 255, 255, 0.72);
}

.summary-card__value {
  color: #ffffff;
}

.upload-workspace {
  margin-top: 0;
}

.upload-panel {
  padding: 1.7rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(245, 250, 246, 0.96)), #ffffff;
  box-shadow: 0 24px 60px rgba(20, 55, 31, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.upload-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.upload-panel__intro {
  flex: 1 1 0;
  min-width: 0;
  max-width: 42rem;
}

.upload-panel__tools {
  flex: 0 0 22rem;
  max-width: 22rem;
}

.section-kicker {
  color: #2c8d41;
}

.upload-panel__header h2 {
  margin: 0 0 0.5rem;
  color: #132218;
}

.upload-panel__header p {
  margin: 0;
  color: #587063;
  line-height: 1.7;
}

.template-card {
  padding: 1rem 1.05rem;
  border-radius: 20px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: linear-gradient(180deg, rgba(250, 252, 250, 0.98), rgba(242, 247, 243, 0.96));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78);
}

.template-card__label {
  display: block;
  margin-bottom: 0.45rem;
  color: #2c8d41;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.template-card strong {
  display: block;
  color: #183322;
  line-height: 1.4;
}

.template-card p {
  margin: 0.45rem 0 0.9rem;
  color: #587063;
  line-height: 1.6;
}

.file-dropzone {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
  padding: 1.35rem;
  border: 1.5px dashed rgba(44, 141, 65, 0.28);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(247, 252, 248, 0.98), rgba(239, 247, 241, 0.96));
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.file-dropzone:hover {
  transform: translateY(-2px);
  border-color: rgba(44, 141, 65, 0.42);
  box-shadow: 0 18px 38px rgba(20, 55, 31, 0.06);
}

.file-dropzone--selected {
  border-style: solid;
  border-color: rgba(44, 141, 65, 0.32);
}

.file-dropzone__input {
  display: none;
}

.file-dropzone__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(48, 171, 76, 0.12);
  color: #27863e;
  font-size: 1.6rem;
}

.file-dropzone__copy h3 {
  margin: 0 0 0.4rem;
  color: #183322;
  font-size: 1.05rem;
}

.file-dropzone__copy p {
  margin: 0;
  color: #587063;
  line-height: 1.65;
}

.upload-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.2rem;
}

.upload-meta-card {
  padding: 1rem 1.05rem;
  border: 1px solid rgba(24, 57, 31, 0.08);
  border-radius: 20px;
  background: #fbfdfb;
}

.upload-meta-card__label {
  display: block;
  color: #2c8d41;
}

.upload-meta-card strong {
  color: #183322;
  line-height: 1.5;
}

.upload-guidance {
  margin-top: 1.25rem;
  padding: 1.15rem 1.2rem;
  border-radius: 22px;
  background: rgba(239, 247, 241, 0.8);
  border: 1px solid rgba(44, 141, 65, 0.08);
}

.upload-guidance__title {
  margin: 0 0 0.75rem;
  color: #183322;
  font-weight: 700;
}

.upload-guidance ul {
  margin: 0;
  padding-left: 1.2rem;
  color: #587063;
  line-height: 1.75;
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.85rem;
  margin-top: 1.35rem;
}

.batch-progress-panel {
  margin-top: 1.35rem;
  padding: 1.15rem;
  border-radius: 24px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.98), rgba(244, 249, 245, 0.96));
}

.batch-progress-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.batch-progress-panel__header h3 {
  margin: 0 0 0.4rem;
  color: #183322;
}

.batch-progress-panel__description {
  margin: 0;
  color: #587063;
  line-height: 1.6;
}

.batch-selector-shell {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.batch-selector-field {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.batch-selector-field__label {
  color: #2c8d41;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.batch-selector-field select {
  width: 100%;
  min-height: 3rem;
  padding: 0.78rem 1rem;
  border: 1px solid rgba(24, 57, 31, 0.12);
  border-radius: 16px;
  background: #ffffff;
  color: #1f3826;
  box-shadow: 0 16px 32px rgba(20, 55, 31, 0.05);
}

.batch-selector-button {
  white-space: nowrap;
}

.batch-empty-state {
  padding: 1rem 1.05rem;
  border-radius: 18px;
  background: rgba(239, 247, 241, 0.8);
  border: 1px solid rgba(44, 141, 65, 0.08);
  color: #587063;
}

.batch-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.batch-status-pill--processing {
  background: rgba(224, 170, 0, 0.14);
  color: #8f6700;
}

.batch-status-pill--success {
  background: rgba(48, 171, 76, 0.12);
  color: #257a3c;
}

.batch-status-pill--danger {
  background: rgba(187, 78, 78, 0.12);
  color: #b14949;
}

.batch-progress-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.batch-progress-bar strong {
  color: #183322;
  white-space: nowrap;
}

.batch-progress-bar__track {
  flex: 1;
  height: 0.8rem;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(24, 57, 31, 0.1);
}

.batch-progress-bar__fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  transition: width 0.3s ease;
}

.batch-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.batch-metric-card {
  padding: 1rem 1.05rem;
  border-radius: 20px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: #ffffff;
}

.batch-metric-card__label {
  display: block;
  margin-bottom: 0.45rem;
  color: #2c8d41;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.batch-metric-card strong {
  display: block;
  color: #183322;
}

.upload-button--template {
  width: 100%;
}

.validation-panel {
  margin-top: 1.35rem;
  padding: 1.15rem;
  border-radius: 24px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.98), rgba(244, 249, 245, 0.96));
}

.validation-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.validation-panel__header h3 {
  margin: 0;
  color: #183322;
}

.validation-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
}

.validation-pill--success {
  background: rgba(48, 171, 76, 0.12);
  color: #257a3c;
}

.validation-pill--danger {
  background: rgba(187, 78, 78, 0.12);
  color: #b14949;
}

.validation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.validation-card {
  padding: 1rem 1.05rem;
  border-radius: 20px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: #ffffff;
}

.validation-card__label {
  display: block;
  margin-bottom: 0.45rem;
  color: #2c8d41;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.validation-card strong {
  display: block;
  margin-bottom: 0.45rem;
  color: #183322;
}

.validation-card p {
  margin: 0;
  color: #587063;
  line-height: 1.6;
}

.validation-alert {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  font-weight: 600;
}

.validation-alert--success {
  background: rgba(48, 171, 76, 0.1);
  color: #257a3c;
}

.validation-alert--danger {
  background: rgba(187, 78, 78, 0.1);
  color: #b14949;
}

.preview-panel {
  margin-top: 1.35rem;
  padding: 1.15rem;
  border-radius: 24px;
  border: 1px solid rgba(24, 57, 31, 0.08);
  background: #fbfdfb;
}

.preview-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.preview-panel__header h3 {
  margin: 0;
  color: #183322;
}

.preview-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(48, 171, 76, 0.1);
  color: #24773a;
  font-size: 0.82rem;
  font-weight: 700;
}

.preview-table-shell {
  overflow: auto;
  border-radius: 18px;
  border: 1px solid rgba(24, 57, 31, 0.06);
}

.preview-empty {
  padding: 1rem;
  text-align: center;
  color: #587063;
}

.preview-panel ::v-deep .preview-table {
  margin-bottom: 0;
}

.preview-panel ::v-deep .preview-table thead th {
  background: #eff6f0;
  border-top: none;
  color: #31513b;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.preview-panel ::v-deep .preview-table tbody td {
  color: #4b6356;
  vertical-align: top;
  font-size: 0.78rem;
  line-height: 1.45;
}

.preview-pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(247, 252, 248, 0.98), rgba(239, 247, 241, 0.96));
  border: 1px solid rgba(24, 57, 31, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.preview-pagination-copy {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.9rem;
  color: #587063;
  font-size: 0.92rem;
}

.preview-pagination-copy p,
.preview-pagination-copy span {
  margin: 0;
}

.preview-page-size-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.preview-page-size-shell select {
  min-width: 4.5rem;
  padding: 0.45rem 0.75rem;
  border: 1px solid rgba(24, 57, 31, 0.12);
  border-radius: 999px;
  background: #f7fbf7;
  color: #1f3826;
  box-shadow: 0 8px 18px rgba(20, 55, 31, 0.05);
}

.preview-panel ::v-deep .pagination {
  margin-bottom: 0;
  gap: 0.35rem;
}

.preview-panel ::v-deep .page-item .page-link {
  min-width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.85rem;
  border: none;
  border-radius: 999px;
  background: rgba(44, 141, 65, 0.1);
  color: #23753c;
  font-weight: 700;
  box-shadow: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.preview-panel ::v-deep .page-item .page-link:hover,
.preview-panel ::v-deep .page-item .page-link:focus {
  background: rgba(44, 141, 65, 0.18);
  color: #184f29;
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(28, 91, 43, 0.12);
}

.preview-panel ::v-deep .page-item.active .page-link {
  color: #ffffff;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 12px 22px rgba(38, 122, 60, 0.2);
}

.preview-panel ::v-deep .page-item.disabled .page-link {
  background: rgba(24, 57, 31, 0.06);
  color: rgba(49, 81, 59, 0.45);
  box-shadow: none;
  transform: none;
}

.review-confirmation {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.95rem 1rem;
  border-radius: 18px;
  background: rgba(239, 247, 241, 0.8);
  border: 1px solid rgba(44, 141, 65, 0.08);
  color: #365345;
  line-height: 1.6;
  cursor: pointer;
}

.review-confirmation input {
  margin-top: 0.2rem;
  accent-color: #2c8d41;
}

.upload-button {
  min-height: 3rem;
  padding: 0.78rem 1.15rem;
  border: 0;
  border-radius: 14px;
  font-weight: 700;
}

.upload-button--ghost {
  background: rgba(20, 38, 26, 0.06);
  color: #1f3826;
}

.upload-button--primary {
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  color: #ffffff;
}

.upload-button--danger {
  background: linear-gradient(135deg, #7f2323 0%, #c14b4b 100%);
  color: #ffffff;
}

.upload-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media screen and (max-width: 991px) {
  .upload-hero__content,
  .upload-meta-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .upload-hero__summary {
    min-width: 0;
    width: 100%;
  }
}

@media screen and (max-width: 767px) {
  .upload-hero {
    padding: 3.5rem 0 2rem;
    border-radius: 0 0 22px 22px;
  }

  .upload-panel {
    padding: 1.15rem;
    border-radius: 22px;
  }

  .upload-panel__header,
  .validation-panel__header,
  .batch-progress-panel__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .batch-selector-shell {
    flex-direction: column;
    align-items: stretch;
  }

  .upload-panel__tools {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }

  .file-dropzone {
    grid-template-columns: 1fr;
    text-align: left;
  }

  .upload-actions {
    flex-direction: column;
  }

  .preview-panel__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .preview-pagination-shell {
    flex-direction: column;
    align-items: flex-start;
  }

  .validation-grid,
  .batch-metrics-grid {
    grid-template-columns: 1fr;
  }

  .batch-progress-bar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
